import React, { useState, useRef, useEffect } from 'react'
import styles from './ProInfo.module.scss'
import BackButton from '../../components/buttons/BackButton/BackButton'
import { useUser } from '../../hooks'
import { useSponsors, useProAgencies, useUpdateTourProInfo } from '../../hooks'
import ProDisplay from '../../components/ProDisplay/ProDisplay'
import Select from 'react-select'
import { uploadS3 } from '../../utils'

const ProInfo = () => {
	const { data: user } = useUser()
	// API는 tourPro 또는 user_tour_pro 로 올 수 있음
	const pro = user?.tourPro ?? user?.user_tour_pro
	const fileInputRef = useRef(null)

	// ProDisplay 컴포넌트를 위한 데이터 구조 변환
	const proDisplayData = user
		? {
				...user,
				user_tour_pro: pro,
			}
		: null

	// API에서 데이터 가져오기
	const { data: sponsorOptions = [] } = useSponsors(pro?.tour_type)
	const { data: agencyOptions = [] } = useProAgencies()

	// 프로 정보 수정 mutation
	const updateTourProInfoMutation = useUpdateTourProInfo()

	// 프로 정보 수정을 위한 state
	const [proData, setProData] = useState({
		tourType: '',
		fullMembershipNumber: '',
		terminationCount: '',
		lastNameEn: '',
		firstNameEn: '',
		experience: '',
		mainSponsor: '',
		agency: '',
		businessRegistration: '',
		top10: '',
		passRate: '',
	})

	useEffect(() => {
		if (!user) return
		setProData({
			tourType: pro?.tour_type || '',
			fullMembershipNumber: pro?.full_membership_number || '',
			terminationCount: pro?.termination_count ?? '',
			lastNameEn: user?.last_name || '',
			firstNameEn: user?.first_name || '',
			experience: pro?.career_level || '',
			mainSponsor: pro?.main_sponsor_id != null ? String(pro.main_sponsor_id) : '',
			agency: pro?.agency_id != null ? String(pro.agency_id) : '',
			businessRegistration: pro?.business_license || '',
			top10: pro?.top10_count ?? '',
			passRate: pro?.cut_through_rate ?? '',
		})
	}, [user, pro])

	// 파일 업로드 상태
	const [uploadedFile, setUploadedFile] = useState(null)

	// Select 옵션들
	const experienceOptions = [
		{ value: 'NEWBIE', label: '신입(1년 미만)' },
		{ value: 'ONE_TO_THREE', label: '1년 ~ 3년차' },
		{ value: 'FOUR_TO_SIX', label: '4년 ~ 6년차' },
		{ value: 'SEVEN_TO_TEN', label: '7년 ~ 10년차' },
		{ value: 'OVER_TEN', label: '10년 이상' },
	]

	// 파일 업로드 핸들러
	const handleFileUpload = async (e) => {
		const file = e.target.files?.[0]
		if (!file) return

		// 파일 타입 및 크기 검증
		const allowedTypes = ['application/pdf', 'image/jpg', 'image/jpeg', 'image/png']
		const isTooLarge = file.size > 5 * 1024 * 1024 // 5MB
		const isInvalidType = !allowedTypes.includes(file.type)

		if (isTooLarge) {
			alert('파일 크기는 5MB 이하여야 합니다.')
			e.target.value = ''
			return
		}

		if (isInvalidType) {
			alert('PDF, JPG, PNG 형식만 업로드 가능합니다.')
			e.target.value = ''
			return
		}

		try {
			setUploadedFile(file)

			// S3에 업로드
			const userId = user?.id || 'temp_' + Date.now()
			const businessURL = await uploadS3(userId, 'business', file)
			console.log('업로드된 S3 URL:', businessURL)

			// 업로드된 URL을 proData에 저장
			setProData({ ...proData, businessRegistration: businessURL })
		} catch (error) {
			console.error('S3 업로드 실패:', error)
			alert('파일 업로드에 실패했습니다. 다시 시도해주세요.')
			setUploadedFile(null)
			e.target.value = ''
		}
	}

	// 파일 업로드 버튼 클릭 핸들러
	const handleUploadButtonClick = () => {
		fileInputRef.current?.click()
	}

	// 저장 핸들러
	const handleSave = async () => {
		try {
			const updateData = {
				first_name: proData.firstNameEn,
				last_name: proData.lastNameEn,
				career_level: proData.experience,
				main_sponsor_id: proData.mainSponsor ? parseInt(proData.mainSponsor) : null,
				agency_id: proData.agency ? parseInt(proData.agency) : null,
				business_license: proData.businessRegistration,
				top10_count: proData.top10 !== '' ? parseInt(proData.top10, 10) : null,
				cut_through_rate: proData.passRate !== '' ? parseFloat(proData.passRate) : null,
			}

			console.log(updateData)

			await updateTourProInfoMutation.mutateAsync(updateData)
			alert('프로 정보가 성공적으로 저장되었습니다.')
		} catch (error) {
			console.error('프로 정보 저장 실패:', error)
			alert('프로 정보 저장에 실패했습니다. 다시 시도해주세요.')
		}
	}

	// console.log(user)

	return (
		<div className={styles.proInfoWrapper}>
			<BackButton />
			<form
				className={styles.proInfoForm}
				onSubmit={(e) => {
					e.preventDefault()
					handleSave()
				}}>
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						gap: '12px',
						alignItems: 'center',
					}}>
					<h2
						style={{
							fontSize: '24px',
							fontWeight: '600',
							color: '#1b1b1b',
							fontFamily: 'Pretendard-SemiBold',
						}}>
						선수 정보
					</h2>

					<span
						style={{
							fontSize: '13px',
							fontWeight: '500',
							color: '#7d7d7d',
							fontFamily: 'Pretendard-Medium',
						}}>
						계정, 연락처, 서비스 동의 등
					</span>
				</div>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '12px',
						marginTop: '40px',
					}}>
					<p
						style={{
							fontSize: '13px',
							fontWeight: '500',
							color: '#7d7d7d',
							fontFamily: 'Pretendard-Medium',
						}}>
						캐디가 제안할 때 보이는 내 프로필이에요
					</p>
					<div style={{ boxShadow: '0px 0px 10px 3px rgba(0, 0, 0, 0.1)' }}>
						<ProDisplay pro={proDisplayData} actionDisabled={true} />
					</div>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							marginTop: '40px',
						}}>
						{/* 읽기 전용 정보들 */}
						{[
							{ label: '소속협회', value: proData?.tourType || '', isFirst: true },
							{
								label: '회원번호',
								value: proData?.fullMembershipNumber || '',
								isFirst: false,
							},
						].map((item, index) => (
							<div
								key={item.label}
								style={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
									alignItems: 'center',
									borderTop: item.isFirst ? '1px solid #e4e4e4' : 'none',
									borderBottom: '1px solid #e4e4e4',
									padding: '12px 0',
								}}>
								<span
									style={{
										fontSize: '14px',
										fontWeight: '600',
										color: '#1b1b1b',
										fontFamily: 'Pretendard-SemiBold',
									}}>
									{item.label}
								</span>
								<span
									style={{
										fontSize: '14px',
										fontWeight: '500',
										color: '#7d7d7d',
										fontFamily: 'Pretendard-Medium',
									}}>
									{item.value}
								</span>
							</div>
						))}

						{/* 영문 성 */}
						<div className={styles.infoBox}>
							<span className={styles.infoLabel}>영문 성</span>
							<div className={styles.fillinBox}>
								<input
									className={styles.fillinInput}
									type="text"
									placeholder="영문 성을 입력해주세요"
									value={proData?.lastNameEn || ''}
									onChange={(e) =>
										setProData({ ...proData, lastNameEn: e.target.value })
									}
								/>
							</div>
						</div>

						{/* 영문 이름 */}
						<div className={styles.infoBox}>
							<span className={styles.infoLabel}>영문 이름</span>
							<div className={styles.fillinBox}>
								<input
									className={styles.fillinInput}
									type="text"
									placeholder="영문 이름을 입력해주세요"
									value={proData?.firstNameEn || ''}
									onChange={(e) =>
										setProData({ ...proData, firstNameEn: e.target.value })
									}
								/>
							</div>
						</div>

						{/* 경력 */}
						<div className={styles.infoBox}>
							<span className={styles.infoLabel}>경력</span>
							<Select
								value={
									experienceOptions?.find(
										(option) => option.value === proData?.experience,
									) || null
								}
								onChange={(selectedOption) =>
									setProData({
										...proData,
										experience: selectedOption?.value || '',
									})
								}
								options={experienceOptions || []}
								placeholder="경력을 선택해주세요"
								isSearchable={false}
								classNamePrefix="custom-select"
							/>
						</div>

						{/* 메인스폰서 */}
						<div className={styles.infoBox}>
							<span className={styles.infoLabel}>메인스폰서</span>
							<Select
								value={
									sponsorOptions?.find(
										(option) => option.value === proData?.mainSponsor,
									) || null
								}
								onChange={(selectedOption) =>
									setProData({
										...proData,
										mainSponsor: selectedOption?.value || '',
									})
								}
								options={sponsorOptions || []}
								placeholder="메인스폰서를 선택해주세요"
								isSearchable={false}
								classNamePrefix="custom-select"
							/>
						</div>

						{/* 에이젼시 */}
						<div className={styles.infoBox}>
							<span className={styles.infoLabel}>에이젼시</span>
							<Select
								value={
									agencyOptions?.find(
										(option) => option.value === proData?.agency,
									) || null
								}
								onChange={(selectedOption) =>
									setProData({ ...proData, agency: selectedOption?.value || '' })
								}
								options={agencyOptions || []}
								placeholder="에이젼시를 선택해주세요"
								isSearchable={false}
								classNamePrefix="custom-select"
							/>
						</div>

						{/* 사업자등록증 */}
						<div className={styles.infoBox}>
							<span className={styles.infoLabel}>사업자등록증</span>
							<div className={styles.fileUploadBox}>
								{uploadedFile ? (
									<span className={styles.fileNameDisplay}>
										{uploadedFile.name}
									</span>
								) : proData.businessRegistration ? (
									<a
										href={proData.businessRegistration}
										target="_blank"
										rel="noopener noreferrer"
										className={styles.fileNameDisplay}
										style={{ color: '#1b1b1b', textDecoration: 'underline' }}>
										등록된 사업자등록증 보기
									</a>
								) : (
									<span className={styles.uploadPlaceholder}>
										파일을 업로드해주세요
									</span>
								)}
								<button
									type="button"
									className={styles.uploadButton}
									onClick={handleUploadButtonClick}>
									파일 업로드
								</button>
								<input
									ref={fileInputRef}
									type="file"
									accept=".pdf,.jpg,.jpeg,.png"
									onChange={handleFileUpload}
									style={{ display: 'none' }}
								/>
							</div>
							<p className={styles.fieldHint}>
								캐디와의 계약 정산 시 세금계산서를 발행하려면 필요해요.
							</p>
						</div>

						{/* TOP 10 (선택) */}
						<div className={styles.infoBox}>
							<span className={styles.infoLabel}>
								TOP 10 <span className={styles.choice}>(선택)</span>
							</span>
							<div className={styles.fillinBox}>
								<input
									className={styles.fillinInput}
									type="text"
									placeholder="TOP 10 순위를 입력해주세요"
									value={proData?.top10 || ''}
									onChange={(e) =>
										setProData({ ...proData, top10: e.target.value })
									}
								/>
							</div>
						</div>

						{/* 통과율 (선택) */}
						<div className={styles.infoBox}>
							<span className={styles.infoLabel}>
								통과율 <span className={styles.choice}>(선택)</span>
							</span>
							<div className={styles.fillinBox}>
								<input
									className={styles.fillinInput}
									type="text"
									placeholder="통과율을 입력해주세요 (%)"
									value={proData?.passRate || ''}
									onChange={(e) =>
										setProData({ ...proData, passRate: e.target.value })
									}
								/>
							</div>
						</div>
					</div>
				</div>
				<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
					<button
						type="submit"
						disabled={updateTourProInfoMutation.isPending}
						style={{
							backgroundColor: updateTourProInfoMutation.isPending
								? '#e4e4e4'
								: 'black',
							color: updateTourProInfoMutation.isPending ? '#999' : 'white',
							padding: '8px 12px',
							borderRadius: '4px',
							fontSize: '12px',
							fontWeight: '600',
							fontFamily: 'Pretendard-Medium',
							cursor: updateTourProInfoMutation.isPending ? 'not-allowed' : 'pointer',
							border: 'none',
						}}>
						{updateTourProInfoMutation.isPending ? '저장 중...' : '저장'}
					</button>
				</div>
			</form>
		</div>
	)
}

export default ProInfo
